import { useCallback, useContext, useRef, useState } from 'react';
import api from '../../../../../api/api';
import { toast } from 'react-toastify';
import DocumentsContext from '../../../../../providers/Documents/Documents.context';
import Button from '../../../../../components/Button';
import Input from '../../../../../components/Input';
import Loader from '../../../../../components/Loader';
import Modal from '../../../../../components/Modal';
import './Admin.styles.scss';

function Admin() {
  const { documents, setDocuments, downloadDocument } =
    useContext(DocumentsContext);
  const [isLoading, setIsLoading] = useState<boolean | string>(false);
  const [isOpenExpirationModal, setIsOpenExpirationModal] = useState<
    boolean | string
  >(false);
  const [expirationDaysValue, setExpirationDaysValue] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const removeDocument = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true);
        await api.removeDocument(id);
        setDocuments((prev) => prev.filter((doc) => doc.id !== id));
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    },
    [setDocuments]
  );

  const handleUploadDocument = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (!file) return;

      try {
        setIsLoading(true);
        const { data } = await api.uploadDocument(file);
        setDocuments((prev) => [...prev, data]);
      } catch (error: any) {
        console.error(error);
        toast.error(
          error.response?.data?.message || 'Error uploading document'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [setDocuments]
  );

  const handleChangeExpirationDays = useCallback(async () => {
    if (!expirationDaysValue) return;

    try {
      const { data } = await api.changeExpirationDays(
        isOpenExpirationModal as string,
        parseInt(expirationDaysValue, 10)
      );
      setDocuments((prev) => prev.map((el) => (el.id === data.id ? data : el)));
      setIsOpenExpirationModal(false);
      setExpirationDaysValue(undefined);
    } catch (e: any) {
      console.error(e);
      toast.error('Changing expiration days failed, please try again.');
    }
  }, [expirationDaysValue, isOpenExpirationModal, setDocuments]);

  return (
    <div className="admin-page">
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleUploadDocument}
      />

      <Modal
        isOpen={!!isOpenExpirationModal}
        onClose={() => {
          setIsOpenExpirationModal(false);
          setExpirationDaysValue(undefined);
        }}
        title="Change expiration time"
      >
        <>
          <p>Enter new document expiration time in days</p>

          <Input
            value={expirationDaysValue}
            onChange={(ev) => {
              const newValue = parseInt(ev.target.value, 10);
              if (Number.isInteger(newValue) || ev.target.value === '')
                setExpirationDaysValue(ev.target.value);
            }}
          />

          <Button
            style={{ marginTop: '12px' }}
            className="normal-button"
            disabled={!expirationDaysValue}
            onClick={handleChangeExpirationDays}
          >
            Save
          </Button>
        </>
      </Modal>

      {isLoading ? (
        <Loader />
      ) : (
        !!documents.length && (
          <div className="content-container">
            <div className="page-header">
              <p className="page-title">Documents configuration</p>
              <Button
                className="normal-button"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload new document
              </Button>
            </div>
            <div className="res-card">
              <div className="config-list">
                <div>
                  <p>File</p>
                  <div>
                    <p>Expiration period</p>
                    <p>Actions</p>
                  </div>
                </div>
                {documents.map((doc) => (
                  <div key={doc.fileKey}>
                    <Button
                      className="list-button"
                      onClick={() => downloadDocument(doc)}
                    >
                      {doc.fileName}
                    </Button>

                    <div>
                      <p
                        className="expiration-label"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsOpenExpirationModal(doc.id);
                        }}
                      >
                        {doc.downloadExpirationDays} days
                      </p>

                      <p
                        className="remove-label"
                        onClick={() => removeDocument(doc.id)}
                      >
                        Remove
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
}

export default Admin;
